import { HttpClient } from "@angular/common/http";
import { Injector } from "@angular/core";
import { UntypedFormGroup } from "@angular/forms";
import { DestroyBase } from "../ng-destroy-base";
import { AppServiceHelperService } from "../../../services/app-service-helper.service";
import { EventBusService } from "../../../services/event-bus.service";
import { Observable, take } from "rxjs";

/**
 * Standardized form base class. Please use this class to implement any form functionality
 */
export abstract class FormBase<T> extends DestroyBase {
  /**
   * RefList API Resource
   */
  private _reflistResource: string;

  /**
   * Retriever API resource
   */
  private _retrieverResource: string;

  /**
  * FormGroup that holds the data, validation etc
  */
  public formData: UntypedFormGroup = null;

  /**
   * Holds reflist data. It can be accessed via this.reflistData.ref_list_name or this.reflistData['ref_list_name']
   */
  public reflistData: any = {};

  /**
   * Retriever data
   */
  public retrieverData: any = {};

  /**
   * Indicates whether the form is ready to be rendered. Please guard the form rendering with *ngIf and show a loading indicator if the value is false
   */
  public formReady = false;

  /**
   * Indicates whether the form is savin. Please show a loading indicator if the value is true and set the save trigger controls to disbled
   */
  public saving = false;

  /**
   * The required ref lists
   */
  protected abstract get requiredReflists(): string[];

  /**
   * The required ref lists
   */
  protected abstract get requiredRetrievers(): string[];

  /**
   * Registers, which ref lists should be loaded
   */
  private _reflistsToLoad: string[] = [];

  /**
   * Registers, which retrievers should be loaded
   */
  private _retrieversToLoad: string[] = [];

  /**
   * Additional promises that should be queried while loading data
   */
  private _additionalLoadPromises: { name: string, promise: Promise<any> }[] = [];

  /**
   * Result of the additional load promises
   */
  protected additionalLoadPromisesResult: any = {};

  /**
   * HTTP client
   */
  protected httpClient: HttpClient;

  /**
   * The event bus
   */
  protected eventBus: EventBusService;

  /**
   * The server helper for getting API URLs
   */
  protected appServiceHelper: AppServiceHelperService;

  /**
   * Constructor
   * @param injector Angular injector
   */
  constructor(protected injector: Injector) {
    super();

    this.eventBus = injector.get(EventBusService);
    this.httpClient = injector.get(HttpClient);
    this.appServiceHelper = injector.get(AppServiceHelperService);

    this._reflistResource = this.appServiceHelper.getServiceEndpoint('pollux.api', 'reflist/pollux');
    this._retrieverResource = this.appServiceHelper.getServiceEndpoint('pollux.api', 'dxsRetriever');
  }

  /**
   * Closes the form
   */
  public abstract close(): void;

  /**
   * Creates the FormGroup and populates it with the data.
   * Important: Please do not parse any Dates from string in this method!
   * @param data Loaded and post-processed data
   */
  protected abstract createFormGroup(data: T): UntypedFormGroup;

  /**
   * Performs post-processing of the data
   * Important: Please do not parse any Dates from string in this method!
   * @param data Loaded data
   */
  protected abstract postProcessData(data: T): Promise<T>;

  /**
   * Use this if you don't need load post-processing
   * @param data
   * @returns Promise
   */
  protected noProcessing(data: T): Promise<T> {
    return Promise.resolve(data);
  }

  /**
   * Use this if you don't need pre-save checks
   * @param data
   * @returns Promise
   */
  protected noPreSave(): Promise<boolean> {
    return Promise.resolve(true);
  }

  /**
   * Use this if you don't need saving
   * @param data
   * @returns Promise
   */
  protected noSave(): Promise<T> {
    return Promise.resolve(null);
  }

  /**
   * Initializes the form and loads the data
   * @returns A Promise that should be awaited
   */
  protected init(): Promise<void> {
    this.requireReflists(this.requiredReflists);
    this.requireRetrievers(this.requiredRetrievers);
    this.formReady = false;
    return new Promise<void>((resolve, reject) => {
      Promise.all([this.loadReflists(), this.loadData(), this.loadAdditionalPromises(), this.loadDefaultRetrievers()])
        .then(([_reflistsResult, data, _additionalPromisesResult, _retrieversResult]) => this.postProcessData(data)).then((data) => {
          this.parseDatesOnLoad(data);
          this.setDefaultRetrieverData(data);
          this.formData = this.createFormGroup(data);
          this.formReady = true;
          resolve();
        }).catch((e) => {
          console.error(e);
          reject(e);
        });
    });
  }

  /**
   * Important: All date values are treated as UTC dates in the backend.
   * Therefore, please parse all date values into a Date by using DateUtil.getDate within this method.
   * This step occurs directly before the createFormGroup step
   * @param data The loaded and post-processed data
   */
  protected abstract parseDatesOnLoad(data: T): void;

  /**
   * Important: All date values are treated as UTC dates in the backend.
   * However, the UI works with TypeScript Dateobjects, which are in the user's local time zone.
   * Therefore, please parse all date values into a UTC ISO strings by using DateUtil.dateToUtcString within this method.
   * This step occurs directly before the saveData step
   * @param data The data that should be saved
   */
  protected abstract formatDatesOnSave(data: T): void;

  /**
   * Requests a reflist to be loaded. Must be called before init() is called
   * @param reflistName Name of the reflist
   */
  protected requireReflist(reflistName: string): void {
    if (this._reflistsToLoad.indexOf(reflistName) < 0) {
      this._reflistsToLoad.push(reflistName);
    }
  }

  /**
   * Requests many reflists to be loaded. Must be called before init() is called
   * @param reflistNames Names of the reflists
   */
  protected requireReflists(reflistNames: string[]): void {
    if (reflistNames?.length) {
      for (const reflistName of reflistNames) {
        this.requireReflist(reflistName);
      }
    }
  }

  /**
   * Requests a retriever to be loaded. Must be called before init() is called
   * @param retrieverName Name of the retriever
   */
  protected requireRetriever(retrieverName: string): void {
    if (this._retrieversToLoad.indexOf(retrieverName) < 0) {
      this._retrieversToLoad.push(retrieverName);
    }
  }

  /**
   * Requests many retrievers to be loaded. Must be called before init() is called
   * @param retrieverNames Names of the retrievers
   */
  protected requireRetrievers(retrieverNames: string[]): void {
    if (retrieverNames?.length) {
      for (const reflistName of retrieverNames) {
        this.requireRetriever(reflistName);
      }
    }
  }

  /**
   * Sometimes you need to load additional data that is related to the actual business object you need to load.
   * An example could be some related data, additional lists, etc.
   * Please attach them to the overall loading process and only make HTTP calls in post processing if absolutely needed,
   * since this will put additional loading times to the total loading time.
   *
   * If added here, these calls will be fired at the same ime as reflist and data retrieval, therefore resulting in an optimal
   * loading time due to concurrency.
   * 
   * Note: You can use the full context here, including all @Input values, but not the data itself.
   * @param name
   * @param promise
   */
  protected getOnLoad(name: string, promise: Promise<any>): void {
    this._additionalLoadPromises.push({ name, promise });
  }

  /**
   * At loading, request loading the actual data via this function.
   * This enables the app to load any reflists / other information in parallel to the actual data
   * @param name
   * @param observable
   */
  protected getObservableOnLoad(name: string, observable: Observable<any>): void {
    const promise = new Promise<any>((resolve, reject) => {
      observable.pipe(take(1)).subscribe({
        next: (data: any) => { resolve(data); },
        error: (error: any) => { reject(error); }
      });
    });
    this._additionalLoadPromises.push({ name, promise });
  }

  /**
   * Loads the data
   */
  protected abstract loadData(): Promise<T>;

  /**
   * Validates the fields.
   * Ideally, you should only use Angular Validators on the FormGroup,
   * but you can also override this method if additional logic is needed
   * @param data The data
   */
  protected validateFields(data: any): boolean {
    if (this.formData.invalid) {
      return false;
    }
    return true;
  }

  /**
   * If a validation error occurs, this ethd is called.
   * Use it to inform the user, enable buttons, etc
   * @param data The data
   */
  public abstract handleValidationError(data: T): void;


  /**
   * Evaluates the save result. Returns true if saving was successful
   * @param result The save result
   */
  public abstract evaluateSaveResult(result: any): boolean;

  /**
   * Saves the data
   * @returns Promise
   */
  public save(): Promise<void> {
    const data: T = this.getSaveData(this.formData, this.formData.getRawValue());
    return new Promise<void>((resolve) => {
      this.saving = true;
      if (this.validateFields(data)) {
        this.handlePreSave(data).then((result) => {
          if (result) {
            this.formatDatesOnSave(data);
            this.saveData(data).then((saveResult: any) => {
              if (this.evaluateSaveResult(saveResult)) {
                this.saving = false;
                this.handlePostSave(saveResult);

                resolve();
              }
              else {
                this.saving = false;
                this.handleSaveError(saveResult);
              }
            }).catch((error: any) => {
              this.saving = false;
              this.handleSaveError(error);
            });
          }
          else {
            this.saving = false;
            this.handleSaveAborted(data);
          }
        }).catch(() => {
          this.saving = false;
          this.handleSaveAborted(data);
        });
      }
      else {
        this.saving = false;
        this.handleValidationError(data);
        resolve();
      }
    });
  }

  /**
   * Perform any pre-save activities here, for example user interaction, user queries etc.
   * If the Promise is resolved with true, saving will continue. If iit's resolved with false or rejected, saving is cancelled.
   * @param data The data that should be saved
   */
  public abstract handlePreSave(data: T): Promise<boolean>;

  /**
   * If the save process was aborted, this methd is called.
   * You can use it to re-enable buttons, inform the user, etc
   * @param data The data that should be saved originally
   */
  public abstract handleSaveAborted(data: T): void;

  /**
   * Saves the data. Additional steps like validation, user interaction etc can also be performed here.
   * The implementation should callt he API endpoint returned by this.getSaveSingleEndpoint(), but can also return EMPTY
   * if nothing should be done.
   * @param data The data to be saved
   */
  protected abstract saveData(data: T): Promise<any>;

  /**
   * Gets the data that should be saved. Please don't do any date parsing/formatting here.
   * Typically, you'll just want to return the rawData parameter directly.
   * @param form The FormGroup
   * @param rawData The FormGroup's raw data
   */
  protected abstract getSaveData(form: UntypedFormGroup, rawData: any): T;

  /**
   * If the save call was triggered, this method is called.
   * Use it to inform the user, close the form or trigger grid reloading.
   * @param saveResult Th result returned from the API
   */
  protected abstract handlePostSave(saveResult: any): void;

  /**
   * If an error occurred during saving, this method is called.
   * Use it to re-enable buttons, inform the user, etc.
   * Note: This occurs on errors and doesn't evaluate the saveResult returned from the API, since this can be different between calls.
   * @param error The error information
   */
  protected abstract handleSaveError(error: any): void;

  /**
   * Loads a reflist by it's name. Can also load config_user and config_business_units
   * @param name The reflist name (ref_xyz)
   */
  private loadReflist(name: string): Promise<any> {
    const url = `${this._reflistResource}?reflistName=${name}&withParentNo=false`;
    return new Promise<any>((resolve, reject) => {
      this.httpClient.get(url).pipe(take(1)).subscribe({
        next: (data: any) => { resolve(data); },
        error: (error: any) => { reject(error); }
      });
    });
  }

  /**
   * Executes and awaits the additional promises. Used for parallel loading of all necessary data
   * @returns A Promise<void> to be awaited
   */
  private loadAdditionalPromises(): Promise<void | Error> {
    return new Promise<void>((resolve, reject) => {
      const promises = [];

      if (this._additionalLoadPromises.length === 0) {
        resolve();
      }
      else {
        for (const additionalLoadPromise of this._additionalLoadPromises) {
          promises.push(additionalLoadPromise.promise);
        }

        Promise.all(promises).then((result: any[]) => {
          const additionalPromisesData = {};

          for (let i = 0; i < this._additionalLoadPromises.length; ++i) {
            additionalPromisesData[this._additionalLoadPromises[i].name] = result[i];
          }

          this.additionalLoadPromisesResult = additionalPromisesData;
          resolve();
        }).catch((e: Error) => {
          reject(e);
        });
      }
    });
  }

  /**
   * Loads a reflist by it's name. Can also load config_user and config_business_units
   * @param name The reflist name (ref_xyz)
   */
  protected loadRetriever(name: string, searchTerm: string): Promise<any> {
    const url = `${this._retrieverResource}/${name}?searchTerm=${searchTerm}`;

    return new Promise<any>((resolve, reject) => {
      this.httpClient.get(url).pipe(take(1)).subscribe({
        next: (data: any) => { resolve(data); },
        error: (error: any) => { reject(error); }
      });
    });
  }

  /**
   * Update retrievers. Can be overridden if needed
   */
  protected abstract updateRetrievers(): void;

  /**
   * Loads the default retriever data
   * @returns A Promise to wait for
   */
  private async loadDefaultRetrievers(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const promises = [];

      if (this._retrieversToLoad.length === 0) {
        resolve();
      }
      else {
        for (const retrieverToLoad of this._retrieversToLoad){
          promises.push(this.loadRetriever(retrieverToLoad, '%%%'));
        }

        Promise.all(promises).then((retrieversResult: any[]) => {
          const retrieverData = {};

          for (let i = 0; i < this._retrieversToLoad.length; ++i) {
            retrieverData[this._retrieversToLoad[i]] = retrieversResult[i].results;
          }

          this.retrieverData = retrieverData;
          resolve();
        }).catch((error) => {
          reject(error);
        });
      }
    });
  }

  /**
   * Loads the reflists
   * @returns A Promise<void> to be awaited
   */
  private async loadReflists(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const promises = [];

      if (this._reflistsToLoad.length === 0) {
        resolve();
      }
      else {
        for (const reflistToLoad of this._reflistsToLoad) {
          promises.push(this.loadReflist(reflistToLoad));
        }

        Promise.all(promises).then((reflistResult: any[]) => {
          const reflistData = {};

          for (let i = 0; i < this._reflistsToLoad.length; ++i) {
            reflistData[this._reflistsToLoad[i]] = this.processReflistResult(this._reflistsToLoad[i], reflistResult[i]);
          }

          this.reflistData = reflistData;
          resolve();
        }).catch((error) => {
          reject(error);
        });
      }
    });
  }

  /**
 * Allows processing / filtering / sorting of reflists
 * @param reflistName The reflist name
 * @param reflistData The reflist data
 * @returns The filtered reflist data
 */
  protected processReflistResult(reflistName: string, reflistData: any[]): any[] {
    return reflistData;
  }

  /**
   * Sets the default retriever data for all retrievers inside the form
   * @param data
   */
  protected abstract setDefaultRetrieverData(data: any): void;
}
